import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiArrowDown, FiArrowLeft, FiCheckCircle } from "react-icons/fi";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { PageHero } from "../components/common/PageHero";
import { useNavigate } from "react-router-dom";
import { SectionWrapper } from "../components/common/SectionWrapper";
import { SERVICES_DATA } from "../data/services";
import { CustomIcon } from "../components/CustomIcon";

export const ServicesPage = () => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue("white", "whiteAlpha.50");
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.100", "gray.700");

  const scrollToServices = () => {
    const el = document.getElementById("services-grid");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box dir="rtl" overflowX="hidden">
      <Navbar />
      <PageHero
        title="خدمات هدفمند با"
        titleHighlight="تأمین سرمایه امید"
        subtitle="هفت حوزه تخصصی برای تامین مالی، پذیرش، بازارگردانی، مدیریت دارایی، مدیریت ریسک و تامین مالی جمعی در یک ساختار منسجم گرد هم آمده‌اند."
        primaryAction={{
          label: "مشاهده خدمات",
          icon: FiArrowDown,
          onClick: scrollToServices,
        }}
        secondaryAction={{
          label: "تماس با ما",
          onClick: () => navigate("/contact"),
        }}
      />

      <SectionWrapper>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {[
              {
                label: "حوزه تخصصی",
                value: SERVICES_DATA.length.toString(),
                helper: "صفحه مستقل برای هر خدمت",
              },
              {
                label: "مدل همراهی",
                value: "۳ مرحله",
                helper: "شناخت نیاز، طراحی ساختار و اجرا",
              },
              {
                label: "خروجی کلیدی",
                value: "مشاوره تا اجرا",
                helper: "از طراحی تا پیگیری عملیاتی",
              },
            ].map((item) => (
              <Box
                key={item.label}
                bg={cardBg}
                borderRadius="2xl"
                p={6}
                border="1px solid"
                borderColor={borderColor}
                textAlign="center"
              >
                <VStack spacing={3}>
                  <Text color={subtitleColor} fontSize="sm">
                    {item.label}
                  </Text>
                  <Heading
                    color={textColor}
                    fontSize={{ base: "2xl", md: "3xl" }}
                  >
                    {item.value}
                  </Heading>
                  <Text color={subtitleColor} fontSize="sm">
                    {item.helper}
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </SectionWrapper>

      <SectionWrapper id="services-grid" alt>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Heading as="h2" fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}>
              سبد خدمات تأمین سرمایه امید
            </Heading>
            <Text color={subtitleColor} maxW="760px">
              هر کارت، درگاه ورود به یک صفحه تخصصی است که توضیحات، حوزه‌های
              خدمت، نمونه کاربردها و مسیر ارتباط با تیم مربوطه را در اختیار
              مخاطب قرار می‌دهد.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
            {SERVICES_DATA.map((service) => (
              <Box
                key={service.id}
                bg={cardBg}
                borderRadius="2xl"
                p={{ base: 6, md: 7 }}
                border="1px solid"
                borderColor={borderColor}
                cursor="pointer"
                _hover={{
                  shadow: "xl",
                  transform: "translateY(-6px)",
                  borderColor: `${service.accentColor}.300`,
                }}
                transition="all 0.3s"
                onClick={() => navigate(`/services/${service.id}`)}
                h="100%"
              >
                <VStack align="start" spacing={4} h="100%">
                  <Box
                    bg={`${service.accentColor}.100`}
                    _dark={{ bg: `${service.accentColor}.200` }}
                    p={3}
                    borderRadius="xl"
                  >
                    <CustomIcon
                      icon={service.icon}
                      size={26}
                      color={`${service.accentColor}.500`}
                    />
                  </Box>
                  <Heading as="h3" fontSize="xl" color={textColor}>
                    {service.title}
                  </Heading>
                  <Text color={subtitleColor} lineHeight="1.9" flex={1}>
                    {service.description}
                  </Text>
                  <Button
                    variant="outline"
                    colorScheme={service.accentColor}
                    rightIcon={
                      <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                    }
                  >
                    مشاهده جزئیات
                  </Button>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </SectionWrapper>

      <SectionWrapper>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Heading as="h2" fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}>
              چگونه همراهی می‌کنیم؟
            </Heading>
            <Text color={subtitleColor} maxW="720px">
              فارغ از نوع خدمت، مسیر همکاری بر پایه شناخت نیاز، طراحی راهکار و
              پیگیری تا اجرا پیش می‌رود.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {[
              {
                title: "شناخت نیاز و ارزیابی اولیه",
                text: "در گام نخست، وضعیت پروژه یا نیاز سازمانی بررسی می‌شود تا دامنه خدمت و سطح آمادگی مشخص شود.",
              },
              {
                title: "طراحی ساختار و برنامه اجرا",
                text: "متناسب با موضوع، مسیر اجرایی، مستندات مورد نیاز و چارچوب تعامل با ذی‌نفعان طراحی می‌شود.",
              },
              {
                title: "همراهی تا پیگیری عملیاتی",
                text: "تیم تخصصی در مرحله پیاده‌سازی، هماهنگی‌ها و ادامه مسیر اجرایی در کنار مشتری باقی می‌ماند.",
              },
            ].map((step) => (
              <Box
                key={step.title}
                bg={cardBg}
                borderRadius="2xl"
                p={6}
                border="1px solid"
                borderColor={borderColor}
              >
                <VStack align="start" spacing={4}>
                  <HStack spacing={3}>
                    <Icon
                      verticalAlign="middle"
                      as={FiCheckCircle as any}
                      color="brand.500"
                      boxSize={5}
                    />
                    <Heading as="h3" fontSize="lg" color={textColor}>
                      {step.title}
                    </Heading>
                  </HStack>
                  <Text color={subtitleColor} lineHeight="1.9">
                    {step.text}
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </SectionWrapper>

      <Footer />
    </Box>
  );
};
